import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Briefcase, Users, Loader2,
  FileText, Clock, Car, CheckCircle2, AlertCircle, CalendarDays,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

function fmtHours(h) {
  if (!h || h <= 0) return "-";
  const hours = Math.floor(h);
  const mins = Math.round((h - hours) * 60);
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
}

function shiftDate(dateStr, days) {
  const d = new Date(dateStr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

const DAY_NAMES_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

function formatDateDE(dateStr) {
  const d = new Date(dateStr + "T12:00:00");
  return `${DAY_NAMES_DE[d.getDay()]}, ${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

export default function MultiProjektBautagesbericht() {
  const navigate = useNavigate();
  const [date, setDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/daily-reports/multi-project?date=${date}`);
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [date]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-4" data-testid="multi-bautagesbericht-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--tga-blue)", opacity: 0.15 }}>
            <FileText className="w-5 h-5" style={{ color: "var(--tga-blue)" }} />
          </div>
          <div>
            <h1 className="text-lg font-bold" style={{ color: "var(--tga-text-primary)" }}>Bautagesbericht</h1>
            <p className="text-xs" style={{ color: "var(--tga-text-muted)" }}>Tagesübersicht aller Projekte mit Mitarbeitern & Stunden</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setDate(d => shiftDate(d, -1))} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="date-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="px-3 py-1.5 text-sm font-semibold rounded-lg" style={{ backgroundColor: "var(--tga-bg-elevated)", color: "var(--tga-text-primary)" }}
            data-testid="date-label">
            {formatDateDE(date)}
          </span>
          <button onClick={() => setDate(d => shiftDate(d, 1))} className="w-8 h-8 flex items-center justify-center rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="date-next">
            <ChevronRight className="w-4 h-4" />
          </button>
          <button onClick={() => setDate(new Date().toISOString().split("T")[0])} className="px-2.5 py-1.5 text-[11px] font-medium rounded-lg border transition-colors hover:bg-white/10"
            style={{ borderColor: "var(--tga-border-strong)", color: "var(--tga-text-secondary)" }} data-testid="date-today">
            Heute
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: "var(--tga-blue)" }} />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Briefcase className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Projekte</span>
              </div>
              <p className="text-2xl font-bold" style={{ color: "var(--tga-text-primary)" }}>{data.totals?.project_count || 0}</p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-emerald-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Mitarbeiter</span>
              </div>
              <p className="text-2xl font-bold text-emerald-400">{data.totals?.worker_count || 0}</p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Arbeitsstunden</span>
              </div>
              <p className="text-2xl font-bold text-blue-400">{fmtHours(data.totals?.work_hours)}</p>
            </div>
            <div className="rounded-xl p-4 border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <div className="flex items-center gap-2 mb-1">
                <Car className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-medium" style={{ color: "var(--tga-text-muted)" }}>Fahrtstunden</span>
              </div>
              <p className="text-2xl font-bold text-amber-400">{fmtHours(data.totals?.travel_hours)}</p>
            </div>
          </div>

          {/* Projects */}
          {(!data.projects || data.projects.length === 0) ? (
            <div className="text-center py-12 rounded-xl border" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}>
              <CalendarDays className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--tga-text-dim)" }} />
              <p className="text-sm" style={{ color: "var(--tga-text-muted)" }}>Keine Aktivitaeten an diesem Tag</p>
            </div>
          ) : (
            <div className="space-y-3">
              {data.projects.map((proj) => (
                <div key={proj.project_id} className="rounded-xl border overflow-hidden" style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}
                  data-testid={`project-card-${proj.project_id}`}>
                  {/* Project header */}
                  <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--tga-border)" }}>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: proj.project_color || "var(--tga-blue)" }} />
                      <div>
                        <p className="text-[14px] font-semibold" style={{ color: "var(--tga-text-bright)" }}>{proj.project_name}</p>
                        <p className="text-[11px]" style={{ color: "var(--tga-text-dim)" }}>{proj.project_type || ""}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" style={{ color: "var(--tga-text-dim)" }} />
                          <span className="text-[12px] font-medium" style={{ color: "var(--tga-text-primary)" }}>{proj.worker_count}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-blue-400" />
                          <span className="text-[12px] font-medium text-blue-400">{fmtHours(proj.work_hours)}</span>
                        </div>
                      </div>
                      {proj.has_report ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-emerald-500/15 text-emerald-400" data-testid={`report-status-${proj.project_id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Bericht
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-500/15 text-amber-400" data-testid={`report-status-${proj.project_id}`}>
                          <AlertCircle className="w-3 h-3" /> Offen
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Workers */}
                  {proj.workers && proj.workers.length > 0 && (
                    <div className="px-4 py-2">
                      <div className="flex flex-wrap gap-2">
                        {proj.workers.map((w, i) => (
                          <div key={`${w.user_id}-${i}`} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px]"
                            style={{ backgroundColor: "var(--tga-bg-elevated)" }}>
                            <span className="font-medium" style={{ color: "var(--tga-text-primary)" }}>{w.user_name}</span>
                            {w.work_start && w.work_end && (
                              <span className="font-mono" style={{ color: "var(--tga-text-dim)" }}>{w.work_start}-{w.work_end}</span>
                            )}
                            {w.net_work_time && (
                              <span className="font-mono text-emerald-400">{w.net_work_time.substring(0, 5)}</span>
                            )}
                            {w.planned && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400">geplant</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
